import React, { useState, useEffect } from 'react';
import MultiSelectTicker from '../MultiSelectTicker';
import { getSortedTickerHistory, trackTickerAccess, formatLastAccessed } from '../../utils/tickerHistory';

function TickerInputSection({ onAnalysisSubmit, watchedStocks, onAddToWatchlist, processing, errors }) {
  const [selectedTickers, setSelectedTickers] = useState([]);
  const [sessionName, setSessionName] = useState('');
  const [saveResults, setSaveResults] = useState(true);
  const [recentTickers, setRecentTickers] = useState([]);

  // Load recent ticker history on mount and listen for updates
  useEffect(() => {
    setRecentTickers(getSortedTickerHistory());

    const handleUpdate = () => {
      setRecentTickers(getSortedTickerHistory());
    };
    window.addEventListener('tickerHistoryUpdated', handleUpdate);
    return () => window.removeEventListener('tickerHistoryUpdated', handleUpdate);
  }, []);

  const handleTickerChange = (selectedOptions) => {
    setSelectedTickers(selectedOptions || []);
  };

  const handleQuickSelect = (ticker) => {
    const exists = selectedTickers.some(t => t.value === ticker);
    if (!exists) {
      setSelectedTickers(prev => [...prev, { label: ticker, value: ticker }]);
    }
  };

  const handleLoadWatchedStocks = () => {
    if (watchedStocks.length > 0) {
      setSelectedTickers(prev => {
        const existingValues = new Set(prev.map(t => t.value));
        const newOnes = watchedStocks
          .map(stock => ({ label: stock.ticker, value: stock.ticker }))
          .filter(t => !existingValues.has(t.value));
        return [...prev, ...newOnes];
      });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (selectedTickers.length === 0) {
      alert('Please enter at least one ticker symbol');
      return;
    }

    const tickers = selectedTickers.map(t => t.value.toUpperCase());

    // Track each ticker in history
    tickers.forEach(ticker => trackTickerAccess(ticker, 'analyzer'));

    const analysisRequest = {
      tickers,
      analysis_types: ['fundamentals', 'eps_trends', 'financials', 'fair_value', 'comparables', 'earnings', 'company_score'],
      session_name: sessionName || '',
      save_results: Boolean(saveResults),
      years: 10
    };

    console.log('Form data being submitted:', analysisRequest);
    onAnalysisSubmit(analysisRequest);
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-white mb-4">Stock Analysis Input</h2>
        <p className="text-gray-400 mb-6">
          Enter ticker symbols to begin comprehensive stock analysis.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Ticker Input */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Ticker Symbols *
          </label>
          <MultiSelectTicker
            value={selectedTickers}
            onChange={handleTickerChange}
            placeholder="Search and select tickers..."
          />
        </div>

        {/* Quick Ticker Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Quick Selection
          </label>

          {/* Recent Searches */}
          <div className="mb-3">
            <div className="text-xs text-gray-400 mb-2">Recent Searches:</div>
            {recentTickers.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {recentTickers.map(item => (
                  <button
                    key={item.ticker}
                    type="button"
                    onClick={() => handleQuickSelect(item.ticker)}
                    className={`px-2 py-1 text-xs rounded transition-colors ${
                      selectedTickers.some(t => t.value === item.ticker)
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    }`}
                    disabled={processing}
                    title={`Last used: ${formatLastAccessed(item.lastAccessed)}`}
                  >
                    {item.ticker}
                  </button>
                ))}
              </div>
            ) : (
              <div className="text-xs text-gray-500 italic">
                No recent searches yet. Your analyzed tickers will appear here.
              </div>
            )}
          </div>

          {/* Watched Stocks */}
          {watchedStocks.length > 0 && (
            <div>
              <div className="text-xs text-gray-400 mb-2 flex items-center justify-between">
                <span>Your Watched Stocks:</span>
                <button
                  type="button"
                  onClick={handleLoadWatchedStocks}
                  className="text-blue-400 hover:text-blue-300 text-xs underline"
                  disabled={processing}
                >
                  Load All
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {watchedStocks.slice(0, 10).map(stock => (
                  <button
                    key={stock.ticker}
                    type="button"
                    onClick={() => handleQuickSelect(stock.ticker)}
                    className={`px-2 py-1 text-xs rounded transition-colors ${
                      selectedTickers.some(t => t.value === stock.ticker)
                        ? 'bg-blue-600 text-white'
                        : 'bg-blue-700 text-blue-100 hover:bg-blue-600'
                    }`}
                    disabled={processing}
                  >
                    {stock.ticker}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Session Options */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Session Name (Optional)
            </label>
            <input
              type="text"
              value={sessionName}
              onChange={(e) => setSessionName(e.target.value)}
              placeholder="e.g., Q4 Tech Stocks Review"
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              disabled={processing}
            />
          </div>

          <div className="flex items-center">
            <label className="flex items-center mt-7">
              <input
                type="checkbox"
                checked={saveResults}
                onChange={(e) => setSaveResults(e.target.checked)}
                className="mr-2 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500"
                disabled={processing}
              />
              <span className="text-white text-sm">Save results to history</span>
            </label>
          </div>
        </div>

        {/* Submit Button */}
        <div className="pt-4">
          <button
            type="submit"
            disabled={processing || selectedTickers.length === 0}
            className="w-full md:w-auto px-8 py-3 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {processing ? (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                Analyzing...
              </div>
            ) : (
              'Start Analysis'
            )}
          </button>
        </div>
      </form>

      {/* Errors */}
      {errors.length > 0 && (
        <div className="bg-red-900/20 border border-red-700 rounded-md p-4">
          <h3 className="text-red-400 font-medium mb-2">Analysis Errors:</h3>
          <ul className="text-red-300 text-sm space-y-1">
            {errors.map((error, index) => (
              <li key={index}>• {error}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default TickerInputSection;
